import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import LoadingState from "../../../components/common/LoadingState";
import NotyfContext from "../../../contexts/NotyfContext";

import ProjectInfomation from "./components/ProjectInfomation";
import ScopeProject from "./components/ScopeProject";
import ScopeProjectByYear from "./components/ScopeProjectByYear";

import {
  getProjectDetails,
  saveRevisedScope,
} from "../../../redux/slices/certify/projectsSlice";
import { setIsLoading } from "../../../redux/slices/projects/projectsSlice";
import {
  getMainProjects,
  getPrograms,
  getProjectScope,
} from "../../../redux/slices/utilsSlice";
import OriginalScopeProject from "./components/OriginalScopeProject";

const MainPage = () => {
  const dispatch = useDispatch();
  const routeParams = useParams();
  const notyf = useContext(NotyfContext);
  const navigate = useNavigate();

  const { projectId } = routeParams;

  const { isLoading, project, revisedProjectScope, inputLoading } = useSelector(
    (state) => ({
      isLoading: state.certifyReducer.isLoading,
      project: state.certifyReducer.project.data,
      revisedProjectScope: state.utilsReducer.certifyFinancial,
      inputLoading: state.utilsReducer.inputLoading,
    }),
  );

  const [disabledBtn, setDisabledBtn] = useState(false);

  const isEqualAmount = useMemo(() => {
    try {
      const totalRevisedCeiling = revisedProjectScope.reduce(
        (acc, obj) => acc + parseFloat(obj.ceiling) || 0,
        0,
      );

      const totalCeilingFlowAmount = revisedProjectScope
        ?.flatMap((obj) => obj.ceiling_flow_details || [])
        ?.reduce((acc, obj) => acc + parseFloat(obj.amount) || 0, 0);

      const difference = totalRevisedCeiling - totalCeilingFlowAmount;

      const groupedByFinancialDetail = revisedProjectScope
        ?.flatMap((obj) => obj.ceiling_flow_details || [])
        .reduce((acc, item) => {
          const financialDetailId = item.financial_detail_id;
          if (!acc[financialDetailId]) {
            acc[financialDetailId] = {
              financial_detail_id: financialDetailId,
              cost_description: item.cost_description,
              ceiling: 0,
              items: [],
            };
          }

          acc[financialDetailId].items.push({
            id: item.id,
            year: item.year,
            amount: item.amount,
            revised_amount: item.revised_amount,
            partial_certify: item.partial_certify,
            ceiling_flow_detail_stages: item.ceiling_flow_detail_stages,
            ceiling_flow_detail_notes: item.ceiling_flow_detail_notes,
          });

          return acc;
        }, {});

      // Convert to array format
      const result = Object.values(groupedByFinancialDetail);

      const balanceCeiling = result.map((obj) => {
        const sumCeilingEachScope = obj.items.reduce(
          (acc, subObj) => acc + parseFloat(subObj.amount) || 0,
          0,
        );
        return {
          ...obj,
          totalCeiling: sumCeilingEachScope,
        };
      });

      if (totalCeilingFlowAmount === totalRevisedCeiling) {
        setDisabledBtn(false);
      }

      return {
        isTrue: totalCeilingFlowAmount !== totalRevisedCeiling,
        isNegativeValue: difference < 0,
        balanceCeilingByScope: balanceCeiling,
      };
    } catch (error) {
      return false;
    }
  }, [revisedProjectScope]);

  const handleGoBack = () => {
    navigate(`/senarai-projek/permohonan-disemak`);
  };

  const handleSaveChanges = async () => {
    if (isEqualAmount.isTrue) {
      showErrorNotification();
      return;
    }

    const params = {
      project_id: project.id,
      data: revisedProjectScope,
    };
    await dispatch(saveRevisedScope(params));
    showNotification();
  };

  const showNotification = () => {
    // Success
    notyf.open({
      type: "success",
      message: "Permohonan disemak berjaya dikemas kini.",
      duration: 3000,
      ripple: false,
      dismissible: true,
      position: {
        x: "right",
        y: "top",
      },
    });
  };

  const showErrorNotification = () => {
    // Success
    let msg = "Terdapat baki siling yang belum ditentukan mengikut tahunan.";

    if (isEqualAmount.isNegativeValue) {
      msg =
        "Baki Aliran siling mengikut tahunan adalah negatif, sila semak amaun siling pada jadual aliran siling";
    }

    notyf.open({
      type: "error",
      message: msg,
      duration: 3000,
      ripple: false,
      dismissible: true,
      position: {
        x: "right",
        y: "top",
      },
    });

    setDisabledBtn(true);
  };

  useEffect(() => {
    const source = axios.CancelToken.source();

    const props = {
      source: source,
      id: routeParams.projectId,
    };

    dispatch(getProjectDetails(props));
    dispatch(getProjectScope(props));
    dispatch(getMainProjects(props));
    dispatch(getPrograms(props));

    return () => {
      source.cancel("Component unmounted, canceling requests");
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      <Helmet title='Permohonan Disemak' />
      <Container fluid className='p-0'>
        <h1 className='h3 mb-3'>Permohonan Disemak</h1>

        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            <h5 className='text-center mb-1 mt-3'>
              {`Keterangan Mengenai Maklumat Projek`.toLocaleUpperCase()}
            </h5>
            <h5 className='text-center mb-1'>
              {`${project.rmk?.name} : ${project?.rmk?.first_year}-${project?.rmk?.fifth_year}`.toLocaleUpperCase()}
            </h5>
            <h5 className='text-center mb-3'>
              {"(Pembiayaan Kerajaan Negeri)".toLocaleUpperCase()}
            </h5>

            <ProjectInfomation />
            <OriginalScopeProject />
            <ScopeProject />
            <ScopeProjectByYear isEqualAmount={isEqualAmount} />
            <div className='d-flex justify-content-end gap-4'>
              <Button variant='light' onClick={handleGoBack}>
                Kembali
              </Button>
              <Button
                variant='success'
                onClick={handleSaveChanges}
                disabled={inputLoading || disabledBtn}
              >
                Simpan
              </Button>
            </div>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default MainPage;
